import { Divider, Stack, useTheme } from '@mui/material';
import React, { PropsWithChildren, useEffect, useReducer, useRef } from 'react';

const UPDATE_DIVIDERS = 'UPDATE_DIVIDERS';
const SCROLL_THRESHOLD = 5;

type Action = { type: typeof UPDATE_DIVIDERS; top: boolean; bottom: boolean };
type State = { top: boolean; bottom: boolean };

const initialState: State = { top: false, bottom: true };

const reducer = (state: State, action: Action): State => {
  if (action.type === UPDATE_DIVIDERS) {
    return { top: action.top, bottom: action.bottom };
  }
  return state;
};

export const ScrollableContainer: React.FC<PropsWithChildren> = ({ children }) => {
  const [dividers, dispatch] = useReducer(reducer, initialState);
  const contentRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const updateDividers = () => {
    if (!contentRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    dispatch({
      type: UPDATE_DIVIDERS,
      top: scrollTop > SCROLL_THRESHOLD,
      bottom: scrollTop + clientHeight < scrollHeight - SCROLL_THRESHOLD,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  useEffect(() => {
    const element = contentRef.current;
    if (element) {
      element.addEventListener('scroll', updateDividers);
      window.addEventListener('resize', updateDividers);

      return () => {
        element.removeEventListener('scroll', updateDividers);
        window.removeEventListener('resize', updateDividers);
      };
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  useEffect(() => {
    updateDividers();
  }, [children]);

  return (
    <Stack sx={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
      <Divider
        sx={{
          opacity: dividers.top ? 1 : 0,
          transition: theme.transitions.create('opacity'),
          mx: 1,
        }}
      />
      <Stack
        ref={contentRef}
        sx={{
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
        onScroll={updateDividers}
      >
        {children}
      </Stack>
      <Divider
        sx={{
          opacity: dividers.bottom ? 1 : 0,
          transition: theme.transitions.create('opacity'),
          mx: 1.5,
        }}
      />
    </Stack>
  );
};
